import type { FC } from 'react';
import { memo, useMemo } from 'react';
import type { GridColDef } from '@mui/x-data-grid';
import HypothesisGroupCard from './HypothesisGroupCard';
import { withCreativeNameTags } from './creativeColumns';
import type { CreativeDnDMovePayload, HypothesisGroup, } from '../../types/hypothesis';
import { type HypothesisPack, MIXED_PACK_KEY } from '../../hooks';
import useGroupSelectionSlices from '../../hooks/useGroupSelectionSlices';

const EMPTY_PACKS: HypothesisPack[] = [];

interface CreativesGroupedViewProps {
  groups: HypothesisGroup[];
  packsByHypothesis: Map<string, HypothesisPack[]>;
  selectedCreatives: string[];
  columns: GridColDef[];
  dragEnabled?: boolean;
  canEditPackTag?: boolean;
  packTagsByIndex?: string[];
  mixedPackCreativeNameTagsById?: Map<string, string[]>;
  onSelectionChange: (hypothesis: string, ids: string[]) => void;
  onPackSelectionChange: (packIndex: number, packCreativeIds: string[]) => void;
  onActivatePack?: (packIndex: number) => void;
  onCreativeMove?: (hypothesis: string, move: CreativeDnDMovePayload) => void;
}

const CreativesGroupedViewImpl: FC<CreativesGroupedViewProps> = ({
  groups,
  packsByHypothesis,
  selectedCreatives,
  columns,
  dragEnabled = false,
  canEditPackTag = false,
  packTagsByIndex,
  mixedPackCreativeNameTagsById,
  onSelectionChange,
  onPackSelectionChange,
  onActivatePack,
  onCreativeMove,
}) => {
  const mixedPacks = packsByHypothesis.get(MIXED_PACK_KEY) ?? EMPTY_PACKS;

  const slices = useGroupSelectionSlices(groups, selectedCreatives);

  const mixedPackColumns = useMemo(
    () => withCreativeNameTags(columns, mixedPackCreativeNameTagsById),
    [columns, mixedPackCreativeNameTagsById],
  );

  return (
    <>
      {mixedPacks.length > 0 && (
        <HypothesisGroupCard
          hypothesis={MIXED_PACK_KEY}
          hideHypothesisHeader
          creatives={[]}
          packs={mixedPacks}
          section={null}
          showSectionSelector={false}
          selectedIds={[]}
          columns={mixedPackColumns}
          dragEnabled={false}
          canEditPackTag={canEditPackTag}
          packTagsByIndex={packTagsByIndex}
          onSelectionChange={onSelectionChange}
          onPackSelectionChange={onPackSelectionChange}
          onActivatePack={onActivatePack}
          onCreativeMove={onCreativeMove}
        />
      )}
      {groups.map((group) => {
        const packs = packsByHypothesis.get(group.hypothesis) ?? EMPTY_PACKS;
        return (
          <HypothesisGroupCard
            key={group.hypothesis}
            hypothesis={group.hypothesis}
            creatives={group.creatives}
            packs={packs}
            section={group.section}
            showSectionSelector={false}
            selectedIds={slices.get(group.hypothesis) ?? []}
            columns={columns}
            dragEnabled={dragEnabled}
            canEditPackTag={canEditPackTag}
            packTagsByIndex={packTagsByIndex}
            onSelectionChange={onSelectionChange}
            onPackSelectionChange={onPackSelectionChange}
            onActivatePack={onActivatePack}
            onCreativeMove={onCreativeMove}
          />
        );
      })}
    </>
  );
};

const CreativesGroupedView = memo(CreativesGroupedViewImpl);

export default CreativesGroupedView;
